import styled from '@emotion/styled';
import {media, Spacer, spacing, Visible} from '@nfq/react-grid';

import {Button} from 'UI/components/action/Button';
import {Form} from 'UI/components/action/Form';
import {Switch} from 'UI/components/action/inputs/Switch';
import {ConsentSmall, H4, P, Small} from 'UI/components/layout/Text';

import {useConsentManager} from './useConsentManager';

/**
 * The `ComponentProps` interface defines the properties for the `ConsentManager` component.
 * This interface specifies the contract for the cookie consent management modal, including a callback function for handling modal closure and a test identifier for automated testing.
 * The interface ensures type safety and provides clear documentation for developers implementing the consent manager functionality.
 */
interface ComponentProps {
    /**
     * A callback function that handles the closing of the consent manager modal.
     * This function is triggered when the user dismisses the modal and can optionally accept a callback function that will be executed after the close operation completes.
     * The callback parameter allows for additional cleanup or state management operations to be performed upon modal closure.
     */
    handleClose(onClose?: () => void): void;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'ConsentManager'
     */
    testId?: string;
}

/**
 * The ConsentManager component provides a user interface for managing cookie consent preferences.
 * It displays different types of cookies (required and analytics) with explanatory text and allows users to toggle their consent for optional cookies.
 * The component adapts its layout and text content based on screen size, providing a responsive experience across different devices.
 *
 * @param props             The component props.
 * @param props.handleClose A function that handles the closing of the consent manager modal, optionally accepting a callback function.
 * @param props.testId      A unique identifier for testing purposes, used to target the component during automated tests.
 * @returns The ConsentManager component JSX containing the cookie consent form with toggleable options.
 *
 * @example
 * ```tsx
 * <ConsentManager
 *     handleClose={(callback) => {
 *         // Close modal logic
 *         callback?.();
 *     }}
 *     testId="cookie-consent-manager"
 * />
 * ```
 */
const ConsentManager = ({handleClose, testId = 'ConsentManager'}: ComponentProps) => {
    const {consent, handleCancel, handleSubmit} = useConsentManager(handleClose);

    return (
        <Wrapper data-cy={testId}>
            <Form id="consent" onSubmit={handleSubmit}>
                <H4>Passen Sie Ihre Cookie-Einstellungen an</H4>
                <Spacer y={2} />
                <Visible lg md xl xxl>
                    <Small>
                        Wir respektieren Ihr Recht auf Privatsphäre. Sie können entscheiden, bestimmte Arten von Cookies
                        nicht zuzulassen. Ihre Cookie-Einstellungen gelten für unsere gesamte Website.
                    </Small>
                </Visible>
                <Visible sm xs>
                    <ConsentSmall>
                        Entscheiden Sie, welche Cookies Sie für diese Website zulassen möchten.
                    </ConsentSmall>
                </Visible>
                <Spacer y={6} />
                <ConsentWrapper>
                    <ConsentRow>
                        <Info>
                            <Small><strong>Erforderlich</strong></Small><br />
                            <Visible lg md xl xxl>
                                <Small>
                                    Benötigt, damit die Website ordnungsgemäß funktioniert. (z.B. dieser Consent-Banner)
                                </Small>
                            </Visible>
                            <Visible sm xs>
                                <ConsentSmall>
                                    Notwendig für Website-Sicherheit und Grundfunktionen.
                                </ConsentSmall>
                            </Visible>
                        </Info>
                        <Status><Tag>Immer an</Tag></Status>
                    </ConsentRow>
                    <ConsentRow>
                        <Info>
                            <Small><strong>Webanalyse</strong></Small><br />
                            <Visible lg md xl xxl>
                                <Small>
                                    Diese Cookies helfen uns zu verstehen, wie unsere Website genutzt wird. Wir erfassen
                                    anonymisierte Informationen über das Nutzerverhalten, um die Funktionalität zu
                                    verbessern, Inhalte zu optimieren und ein besseres Nutzungserlebnis zu ermöglichen.
                                </Small>
                            </Visible>
                            <Visible sm xs>
                                <ConsentSmall>
                                    Anonyme Website-Analyse zur Funktionsverbesserung.
                                </ConsentSmall>
                            </Visible>
                        </Info>
                        <Status>
                            <Switch initialValue={consent.statistics ?? false} name="analytics" />
                        </Status>
                    </ConsentRow>
                </ConsentWrapper>
                <Spacer y={8} />
                <ButtonWrapper>
                    <Button as="button" size="small" variant="secondary" onClick={handleCancel}>Abbrechen</Button>
                    <Button as="button" size="small" type="submit">Speichern</Button>
                </ButtonWrapper>
            </Form>
        </Wrapper>
    );
};

ConsentManager.displayName = 'ConsentManager';

export {ConsentManager};

const Wrapper = styled.div`
    padding: ${spacing(8)};
`;

const ConsentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing(4)};
`;

const ConsentRow = styled.div`
    align-items: start;
    display: flex;
    gap: ${spacing(4)};
    justify-content: space-between;
`;

const Info = styled.div`
    flex: 1 1 auto;
`;

const Status = styled.div`
    display: flex;
    flex: 0 0 10rem;
    justify-content: center;
`;

const Tag = styled(P)`
    background-color: ${({theme}) => theme.colors.backgroundNeutral};
    border-radius: 9999px;
    padding: ${spacing(1)} ${spacing(2)};
    text-align: center;
`;

const ButtonWrapper = styled.div`
    align-items: stretch;
    display: flex;
    flex-direction: column-reverse;
    gap: ${spacing(4)};
    justify-content: end;

    & > button {
        justify-content: center;
        width: 100%;
    }

    ${media('sm')} {
        align-items: stretch;
        flex-direction: row;

        & > button {
            width: fit-content;
        }
    }
`;